import { useEffect, useState } from 'react'

import { trackEvent } from '../utils/analytics'

// android keeps the event name it was launched under, so the funnel already
// reporting on it stays continuous.
const STORES = {
	ios: {
		url: 'https://apps.apple.com/app/id6801409601',
		ctaLocation: 'ios_app_banner',
	},
	android: {
		url: 'https://play.google.com/store/apps/details?id=com.loadfetcher.app',
		ctaLocation: 'android_app_banner',
	},
}

// Still the Android-era key so phones that already dismissed the banner are
// not pitched to a second time.
const DISMISSED_KEY = 'loadfetcher:android-app-banner'

/**
 * Which app this phone should be offered, or null for anything we do not ship
 * an app for.
 *
 * Every Android user agent carries "Android" and no iOS one ever does. The wv
 * token marks an Android WebView, which is our own app, so it is excluded.
 * iPadOS reports itself as a Mac, so a Macintosh that reports touch points is
 * really an iPad.
 */
const platformForBrowser = () => {
	if (typeof navigator === 'undefined') return null

	const ua = navigator.userAgent || ''
	if (/Android/i.test(ua)) return /\bwv\b/.test(ua) ? null : 'android'

	const isIPadOS = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1
	if (/iPhone|iPad|iPod/.test(ua) || isIPadOS) return 'ios'

	return null
}

const wasDismissed = () => {
	// localStorage throws in private mode; the banner showing again is harmless
	try {
		return localStorage.getItem(DISMISSED_KEY) === '1'
	} catch (err) {
		return false
	}
}

/**
 * Shown once per phone, until it is dismissed for good. Brand blue
 * rather than the page's white, so it reads as a banner and not as content.
 *
 * The id is how the fixed mobile navbars find it and sit below it; dismissing
 * fires lf:install-banner so they close the gap without waiting for a scroll.
 */
function AppInstallBanner() {
	const [platform, setPlatform] = useState(null)

	useEffect(() => {
		const detected = platformForBrowser()
		if (detected && !wasDismissed()) setPlatform(detected)
	}, [])

	// Runs after the banner is in the DOM, so the fixed navbars can measure it.
	// Showing and hiding both land here; on first mount it is a no-op.
	useEffect(() => {
		window.dispatchEvent(new Event('lf:install-banner'))
	}, [platform])

	if (!platform) return null

	const hide = () => {
		try {
			localStorage.setItem(DISMISSED_KEY, '1')
		} catch (err) {}
		setPlatform(null)
	}

	const openStore = () => {
		trackEvent('cta_click', { cta_location: STORES[platform].ctaLocation })
		hide()
	}

	return (
		<div id="app-install-banner" className="relative bg-blue-600 px-3 py-2.5">
			<div className="mx-auto flex max-w-2xl items-center gap-3">
				<span className="flex h-9 w-9 flex-none items-center justify-center overflow-hidden rounded-xl bg-white">
					<img
						src={`${process.env.PUBLIC_URL}/logo192.png`}
						alt=""
						className="h-full w-full object-contain"
					/>
				</span>

				<p className="min-w-0 flex-1 truncate text-sm font-semibold text-white">
					Get the app
				</p>

				<a
					href={STORES[platform].url}
					target="_blank"
					rel="noopener noreferrer"
					onClick={openStore}
					className="flex min-h-[36px] flex-none items-center whitespace-nowrap rounded-full bg-white px-5 text-sm font-bold text-blue-700 hover:bg-blue-50"
				>
					Install
				</a>

				<button
					type="button"
					onClick={hide}
					aria-label="Hide this for good"
					className="-mr-1 ml-2 flex h-8 w-8 flex-none items-center justify-center rounded-md text-white/70 hover:bg-white/10 hover:text-white"
				>
					<svg
						className="h-5 w-5"
						fill="none"
						viewBox="0 0 24 24"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						aria-hidden="true"
					>
						<path d="M6 6l12 12M18 6L6 18" />
					</svg>
				</button>
			</div>
		</div>
	)
}

export default AppInstallBanner
